import { Spinner } from '@toggle/design-system';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Indicator } from '~/api/timeseries/timeseries-schema';
import { IndicatorsChart } from '~/components/indicators-chart/IndicatorsChart';
import { mapEntities, useEntitiesWithReactQuery } from '~/hooks/use-entities';
import { SPY_ARCA_ENTITY, useSpyArca } from '~/hooks/useSpyArca/useSpyArca';
import { useIndicatorStore } from '~/stores/use-indicator/useIndicator';
import {
  widgetPaths,
  widgetSearchParamKeys,
} from '~/views/widgets/widget-utils';

import { ChartSpy } from '../chart-spy/ChartSpy';
import { ErrorMessageWrapper } from '../error-message-wrapper/ErrorMessageWrapper';
import * as S from './ChartWidget.styles';

const getActiveIndicator = (searchParams: URLSearchParams) =>
  (searchParams.get(widgetSearchParamKeys.indicator) as Indicator | null) ??
  Indicator.TLI;

export const ChartWidgets = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeIndicator, setActiveIndicator] = useState<Indicator>(() =>
    getActiveIndicator(searchParams)
  );
  const { initializeData, indicatorValues, update } = useIndicatorStore(
    state => ({
      initializeData: state.initialize,
      indicatorValues: state.indicators,
      update: state.update,
    })
  );
  const { tsData, thresholdsDates } = indicatorValues;
  const tsDataTli = tsData?.[activeIndicator] || [];

  useEffect(() => {
    if (!indicatorValues) {
      initializeData();
    }
  }, []);

  useEffect(() => {
    update(activeIndicator);
  }, [activeIndicator, update]);

  const { data: entities } = useEntitiesWithReactQuery([SPY_ARCA_ENTITY]);
  const mappedEntities = mapEntities(entities);

  const {
    tsData: tsDataSpyArca,
    priceSuffix,
    name,
    snakeMeta,
    refetch,
    loading: loadingSpyArca,
  } = useSpyArca({ thresholdsDates });

  useEffect(() => {
    if (searchParams) {
      const activeIndicator = getActiveIndicator(searchParams);
      setActiveIndicator(activeIndicator);
    }
  }, [searchParams]);

  if (!mappedEntities || loadingSpyArca) {
    return (
      <S.LoaderWrapper>
        <Spinner />
      </S.LoaderWrapper>
    );
  }

  return (
    <>
      <IndicatorsChart
        tsData={tsDataTli}
        onChange={(_, tabKey) =>
          setSearchParams({
            widget: widgetPaths.indicatorTadpole,
            [widgetSearchParamKeys.indicator]: tabKey,
          })
        }
        activeIndicator={activeIndicator}
      />
      {mappedEntities?.[0] && tsDataSpyArca.length > 0 ? (
        <ChartSpy
          title={name}
          tsData={tsDataSpyArca}
          entity={mappedEntities[0]}
          priceSuffix={priceSuffix}
          snakeMeta={snakeMeta}
          activeIndicator={activeIndicator}
        />
      ) : (
        <S.ErrorWrapper>
          <ErrorMessageWrapper onClick={refetch} displayIcon />
        </S.ErrorWrapper>
      )}
    </>
  );
};
